import React, {Suspense} from "react";
import * as Sentry from "@sentry/react";
import {ErrorBoundary} from "@sentry/react";
import {ErrorMessage} from "./components/errorMessage";
import {useAddToRecentlyViewed} from "./hooks/useAddToRecentlyViewed";
import {BreadCrumbs} from "./components/breadCrumbs";
import {Button, Card, Col, Container, Row} from "react-bootstrap";
import {CrossSells} from "./areas/product/crossSells/crossSells";
import {BrandLink} from "./areas/product/details/brandLink";
import {StarRating} from "./components/starRating";
import {Gallery as MediaGallery} from "./areas/product/media/gallery";
import {ShareLinks} from "./areas/product/social/shareLinks";
import {PricingTable} from "./areas/product/purchase/pricingTable";
import {CustomizableNotice} from "./areas/product/userContent/customizableNotice";
import {ProductOptions} from "./areas/product/details/productOptions";
import {DynamicDimensions} from "./areas/product/purchase/dynamicDimensions";
import {InstallationService} from "./areas/product/purchase/installationService";
import {Warranty} from "./areas/product/purchase/warranty";
import {AddToCart} from "./areas/product/purchase/cart/addToCart";
import {Disclaimers} from "./areas/product/details/disclaimers";
import {ComparisonTable} from "./areas/product/details/comparisonTable";
import {CouponCode} from "./areas/product/purchase/couponCode";
import {RequestAQuote} from "./areas/product/purchase/requestAQuote";
import {PriceBox} from "./areas/product/purchase/price";
import {IncludedWithProduct} from "./areas/product/details/accordion/components/includedWithProduct";
import {BulletPoints} from "./areas/product/details/accordion/components/bulletPoints";
import {Content as Accordion} from "./areas/product/details/accordion/content";
import {SkeletonCard} from "./components/skeletonCard";
import Skeleton from "react-loading-skeleton";
import {StockInformationContainer} from "./areas/product/purchase/stockInformation";
import {ProductStatusContainer} from "./areas/product/purchase/productStatus";
import {useLeftColumnModel} from "./hooks/useLeftColumnModel";
import {ListItemTransactionToast} from "./components/listItemTransactionToast";
import {useProductDetailsQuery} from "./hooks/useProductDetailsQuery";
import {useHistoryListener} from "./hooks/useHistoryListener";
import {EmployeeNavigation} from "./areas/product/internal/employeeNavigation";
import {ArtworkOffCanvas} from "./areas/product/purchase/cart/artworkOffCanvas";
import {AddToCartOffCanvas} from "./areas/product/purchase/cart/addToCartOffCanvas";
import {SkeletonGallery} from "./components/skeletonGallery";
import {HighLevelCallout} from "./areas/product/details/highLevelCallout";
import {EmployeeNotificationBanner} from "./components/employeeNotificationBanner";
import 'react-multi-carousel/lib/styles.css';
import "./styles/app.scss";

const App = () => {
    useAddToRecentlyViewed();
    useHistoryListener();

    return (
        <ErrorBoundary showDialog={true} fallback={props => <ErrorMessage {...props} />}>
            <main>
                <ErrorBoundary fallback={<></>}>
                    <Suspense fallback={<></>}>
                        <EmployeeNotificationBanner/>
                    </Suspense>
                </ErrorBoundary>
                <Container fluid="lg" className="mt-3" style={{height: '67px'}}>
                    <Row>
                        <Col>
                            <ErrorBoundary fallback={<></>}>
                                <Suspense fallback={<Skeleton height={67}/>}>
                                    <BreadCrumbs/>
                                </Suspense>
                            </ErrorBoundary>
                        </Col>
                    </Row>
                </Container>
                <Container fluid="lg">
                    <Row className="mb-5">
                        <Col>
                            <Row>
                                <LeftColumn/>
                                <RightColumn/>
                            </Row>
                        </Col>
                    </Row>
                </Container>
                <Container fluid="lg">
                    <ErrorBoundary fallback={<></>}>
                        <Row className="mx-auto d-block my-3">
                            <Suspense fallback={<Col><Skeleton height={382.63}/></Col>}>
                                <HighLevelCallout/>
                            </Suspense>
                        </Row>
                    </ErrorBoundary>
                    <ErrorBoundary fallback={<></>}>
                        <Accordion/>
                    </ErrorBoundary>
                    <Suspense fallback={<></>}>
                        <ErrorBoundary fallback={<></>}>
                            <Disclaimers/>
                        </ErrorBoundary>
                    </Suspense>
                    <ComparisonTable/>
                </Container>
                <CrossSells/>
                <EmployeeNavigation/>
                <Suspense fallback={<SkeletonCard/>}>
                    <AddToCartOffCanvas/>
                </Suspense>
                <Suspense fallback={<SkeletonCard/>}>
                    <ArtworkOffCanvas/>
                </Suspense>
            </main>
        </ErrorBoundary>
    )
}

const LeftColumn = () => {
    const {
        title,
        ratingStatistics: {
            grade,
            count,
            onClick
        }
    } = useLeftColumnModel();

    return (
        <Col id="leftBuyColumn" md={6} className="bg-white">
            <div className="sticky-wrap">
                <Row style={{minHeight: '140.34px'}}>
                    <Col xs={12} className="d-flex flex-column py-2">
                        <Suspense fallback={<></>}>
                            <ErrorBoundary fallback={<></>}>
                                <BrandLink/>
                            </ErrorBoundary>
                        </Suspense>
                        <div style={{minHeight: '89.25px'}} className="d-flex flex-row align-items-center">
                            <h1 className="fs-2 fw-normal my-0 text-dark">
                                <Suspense fallback={<>{title}</>}>
                                    <ErrorBoundary fallback={<></>}>
                                        <Title/>
                                    </ErrorBoundary>
                                </Suspense>
                            </h1>
                        </div>
                        {count > 0 && (
                            <ErrorBoundary fallback={<></>}>
                                <Button variant="link" className="review-statistics ps-0" onClick={onClick}>
                                <span className="review-stars-wrap">
                                <StarRating grade={grade}/>
                                <span className="review-text">
                                    <strong className="review-ratings-total">{grade}{' '}</strong>
                                    <span className="review-ratings-count">
                                        out of <strong>5</strong> | ({count} Ratings)
                                    </span>
                                    </span>
                                </span>
                                </Button>
                            </ErrorBoundary>
                        )}
                    </Col>
                </Row>
                <ErrorBoundary fallback={<></>}>
                    <Suspense fallback={<SkeletonGallery/>}>
                        <MediaGallery/>
                    </Suspense>
                </ErrorBoundary>
                <Row style={{height: '70px'}}>
                    <Col>
                        <ErrorBoundary fallback={<></>}>
                            <Suspense fallback={
                                <div className="my-3 d-flex justify-content-end">
                                    <Skeleton height={22} width={34} className="mx-1"/>
                                    <Skeleton height={22} width={34} className="mx-1"/>
                                    <Skeleton height={22} width={34} className="mx-1"/>
                                    <Skeleton height={22} width={34} className="mx-1"/>
                                </div>}>
                                <ShareLinks/>
                            </Suspense>
                        </ErrorBoundary>
                    </Col>
                </Row>
            </div>
        </Col>
    );
};

const RightColumn = () => {
    return (
        <Col id="rightBuyColumn" md={6}>
            <div className="overflow">
                <Row className="bg-light pt-3 mt-sm-3">
                    <Col>
                        <Card body>
                            <div className="d-flex align-items-start justify-content-between"
                                 style={{minHeight: '2.5rem'}}>
                                <Suspense
                                    fallback={<Skeleton count={.5} height={34} containerClassName="flex-fill w-100"/>}>
                                    <PriceBox/>
                                </Suspense>
                                <ErrorBoundary fallback={<></>}>
                                    <RequestAQuote/>
                                </ErrorBoundary>
                            </div>
                            <PricingTable/>
                            <Suspense fallback={<></>}>
                                <ErrorBoundary fallback={<></>}>
                                    <CouponCode/>
                                </ErrorBoundary>
                            </Suspense>
                        </Card>
                        <Suspense fallback={<SkeletonCard/>}>
                            <StockInformationContainer/>
                        </Suspense>
                        <Suspense fallback={<SkeletonCard/>}>
                            <ErrorBoundary fallback={<></>}>
                                <Card body className="my-3">
                                    <IncludedWithProduct/>
                                    <ul>
                                        <ProductStatusContainer/>
                                        <BulletPoints numberOfBulletPointsToRender={3} showViewAllLink={true}/>
                                    </ul>
                                </Card>
                            </ErrorBoundary>
                        </Suspense>
                        <Suspense fallback={<></>}>
                            <ErrorBoundary fallback={<></>}>
                                <CustomizableNotice/>
                            </ErrorBoundary>
                        </Suspense>
                        <Suspense fallback={<SkeletonCard/>}>
                            <ErrorBoundary fallback={<></>}>
                                <ProductOptions/>
                            </ErrorBoundary>
                        </Suspense>
                        <Suspense fallback={<></>}>
                            <DynamicDimensions/>
                        </Suspense>
                        <Suspense fallback={<></>}>
                            <ErrorBoundary fallback={<></>}>
                                <InstallationService/>
                            </ErrorBoundary>
                        </Suspense>
                        <Suspense fallback={<></>}>
                            <ErrorBoundary fallback={<></>}>
                                <Warranty/>
                            </ErrorBoundary>
                        </Suspense>
                        <Suspense fallback={<SkeletonCard/>}>
                            <AddToCart/>
                        </Suspense>
                        <ErrorBoundary fallback={<></>}>
                            <ListItemTransactionToast/>
                        </ErrorBoundary>
                    </Col>
                </Row>
            </div>
        </Col>
    );
};

const Title = () => {
    const {model: {title}} = useProductDetailsQuery();
    return (
        <>{title}</>
    );
};

export default Sentry.withProfiler(App);